import { useCallback, useEffect, useRef, useState } from "react";

interface AudioWaveformProps {
  volume: number; // 0.0 à 1.0
  isActive: boolean;
  className?: string;
  compact?: boolean; // Mode compact pour l'indicateur intégré
}

// Configuration constants
const BAR_COUNT = 25; // Number of bars/dots to display
const SILENCE_THRESHOLD = Number(
  process.env.BUN_PUBLIC_SILENCE_THRESHOLD || 0.05,
); // Volume below this = silence (from env)
const BAR_MAX_HEIGHT = 28; // Maximum bar height in pixels (normal mode)
const BAR_MAX_HEIGHT_COMPACT = 12; // Maximum bar height in pixels (compact mode)
const BAR_MIN_HEIGHT = 4; // Minimum bar height in pixels
const BAR_MIN_HEIGHT_COMPACT = 2; // Minimum bar height in pixels (compact mode)
const UPDATE_INTERVAL_MS = Number(
  process.env.BUN_PUBLIC_AUDIO_UPDATE_INTERVAL_MS || 50,
); // How often to update the visualization

export function AudioWaveform({
  volume,
  isActive,
  className = "",
  compact = false,
}: AudioWaveformProps) {
  // Store volume history for the bar visualization
  const [volumeHistory, setVolumeHistory] = useState<number[]>(() =>
    Array(BAR_COUNT).fill(0),
  );

  // Track if we're in silent state (with slight delay for smoother transitions)
  const [isSilent, setIsSilent] = useState(true);
  const silenceTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastVolumeRef = useRef<number>(0);

  // Smooth volume tracking
  const smoothedVolumeRef = useRef<number>(0);

  // Update volume history periodically when recording
  const updateVisualization = useCallback(() => {
    if (!isActive) {
      setVolumeHistory(Array(BAR_COUNT).fill(0));
      setIsSilent(true);
      return;
    }

    // Apply smoothing to volume
    smoothedVolumeRef.current += (volume - smoothedVolumeRef.current) * 0.4;
    const smoothVolume = smoothedVolumeRef.current;
    lastVolumeRef.current = smoothVolume;

    // Determine if we're in silent state
    const currentlySilent = smoothVolume < SILENCE_THRESHOLD;

    if (currentlySilent) {
      // Delay transition to silent state for smoother experience
      if (!silenceTimeoutRef.current) {
        silenceTimeoutRef.current = setTimeout(() => {
          setIsSilent(true);
        }, 150);
      }
    } else {
      // Clear any pending silence timeout and immediately show bars
      if (silenceTimeoutRef.current) {
        clearTimeout(silenceTimeoutRef.current);
        silenceTimeoutRef.current = null;
      }
      setIsSilent(false);
    }

    // Update volume history - shift left and add new value
    setVolumeHistory((prev) => {
      const newHistory = [...prev.slice(1), smoothVolume];
      return newHistory;
    });
  }, [isActive, volume]);

  // Set up interval for updates
  useEffect(() => {
    // Reset smoothing when recording starts
    if (isActive) {
      smoothedVolumeRef.current = 0;
    }

    const interval = setInterval(updateVisualization, UPDATE_INTERVAL_MS);

    return () => {
      clearInterval(interval);
      if (silenceTimeoutRef.current) {
        clearTimeout(silenceTimeoutRef.current);
        silenceTimeoutRef.current = null;
      }
    };
  }, [isActive, updateVisualization]);

  // Reset when not active
  useEffect(() => {
    if (!isActive) {
      setVolumeHistory(Array(BAR_COUNT).fill(0));
      setIsSilent(true);
      smoothedVolumeRef.current = 0;
    }
  }, [isActive]);

  // Calculate bar height based on volume
  const getBarHeight = (vol: number): number => {
    const minHeight = compact ? BAR_MIN_HEIGHT_COMPACT : BAR_MIN_HEIGHT;
    const maxHeight = compact ? BAR_MAX_HEIGHT_COMPACT : BAR_MAX_HEIGHT;

    if (vol < SILENCE_THRESHOLD) {
      return minHeight;
    }
    // Map volume to height range
    const normalizedVolume =
      (vol - SILENCE_THRESHOLD) / (1 - SILENCE_THRESHOLD);
    return minHeight + normalizedVolume * (maxHeight - minHeight);
  };

  // Container height based on mode
  const containerHeight = compact ? "h-4" : "h-8";
  // Bar color: white in compact mode (on red background), red in normal mode
  const barColor = compact ? "bg-white" : "bg-red-500";
  const dotColor = compact ? "bg-white/60" : "bg-gray-400 dark:bg-gray-500";
  const inactiveColor = compact
    ? "bg-white/30"
    : "bg-gray-300 dark:bg-gray-600";

  // If not recording at all, show flat line
  if (!isActive) {
    return (
      <div
        className={`flex items-center justify-center ${containerHeight} gap-0.5 ${className}`}
      >
        {Array.from({ length: BAR_COUNT }).map((_, i) => (
          <div
            key={i}
            className={`w-1 rounded-full ${inactiveColor} self-center`}
            style={{ height: "2px" }}
          />
        ))}
      </div>
    );
  }

  // Recording active - show either dots or bars
  return (
    <div
      className={`flex items-center justify-center ${containerHeight} gap-0.5 overflow-hidden ${className}`}
    >
      {volumeHistory.map((vol, i) => {
        const height = getBarHeight(vol);

        // Silent state: show small dots with pulse animation
        if (isSilent) {
          return (
            <div
              key={i}
              className={`w-1 h-1 rounded-full ${dotColor} animate-pulse self-center`}
              style={{ animationDelay: `${i * 30}ms` }}
            />
          );
        }

        // Active state: show bars with height based on volume
        return (
          <div
            key={i}
            className={`w-1 rounded-full ${barColor} transition-all duration-75 ease-out self-center`}
            style={{ height: `${height}px` }}
          />
        );
      })}
    </div>
  );
}
